import {
  FormHelperText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Hue, IList, isInRange, tuple } from 'annotation-tools/utils';
import React, { FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { maxStringLength } from '../../../constants/max-string-length';
import { colorDef } from '../../../constants/styles/color-def';
import { viewTexts } from '../../../constants/view-texts';
import { ILabelFromApiType } from '../../../types/record/label';
import { MuiButton, MuiInput } from '../../shared/form-element';
import { MessageArea } from '../../shared/message-area';
import { RemoveCircleIcon } from '../../shared/mui/icons/remove';
import { MuiPaper } from '../../shared/mui/mui-paper';
import { MuiTableRow } from '../../shared/mui/mui-table-row';
import { Root } from '../../shared/styled/root';
import { LabelColorCell } from './color-cell/color-cell';
import { hexToHue } from 'annotation-tools/utils';

const vt = viewTexts.pages.labelList;

const ButtonArea = styled.div`
  display: flex;
  flex-direction: row;
  padding: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 5px;
`;

const TitleBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Description = styled.div`
  padding: 5px;
`;

const TableCellDelete = styled(TableCell)`
  width: 1px;
`;

const ErrorArea = styled.div`
  padding: 10px;
`;

const MessageAreaWrapper = styled.div`
  padding: 20px 0;
`;

const message = [vt.messages.labelIsNotDeletable];

interface Props {
  mode: 'edit' | 'view';
  labelList: IList<ILabelFromApiType>;
  editOnClick: () => void;
  cancelOnClick: () => void;
  saveOnClick: () => void;
  addLabelOnClick: () => void;
  labelNameOnChange: (id: number, value: string) => void;
  labelColorOnChange: (id: number, value: Hue) => void;
  deleteLabel: (id: number) => void;
  showErrorString: boolean;
  labelNamesAreValid: boolean;
}

export const LabelsView: FC<Props> = memo(
  ({
    mode,
    labelList,
    editOnClick,
    cancelOnClick,
    saveOnClick,
    addLabelOnClick,
    labelNameOnChange,
    labelColorOnChange,
    deleteLabel,
    showErrorString,
    labelNamesAreValid
  }: Readonly<Props>) => {
    const labelsWithHandler = useMemo(
      () =>
        labelList.map(lbl =>
          tuple(
            lbl,
            (name: string) => labelNameOnChange(lbl.id, name),
            () => deleteLabel(lbl.id)
          )
        ),
      [labelList, labelNameOnChange, deleteLabel]
    );

    const labelsCanBeDeleted = useMemo<boolean>(
      () => labelList.every(l => l.canBeDeleted),
      [labelList]
    );

    function changeLabelColor(id: number, colorCode: string) {
      labelColorOnChange(id, hexToHue(colorCode));
    }

    return (
      <Root>
        <TitleBar>
          <ButtonArea>
            {mode === 'view' ? (
              <ButtonWrapper>
                <MuiButton
                  label={vt.buttons.edit}
                  color='primary'
                  onClick={editOnClick}
                />
              </ButtonWrapper>
            ) : (
              <>
                <ButtonWrapper>
                  <MuiButton
                    label={vt.buttons.discard}
                    color='secondary'
                    onClick={cancelOnClick}
                  />
                </ButtonWrapper>
                <ButtonWrapper>
                  <MuiButton
                    label={vt.buttons.save}
                    color='primary'
                    onClick={saveOnClick}
                    disabled={!labelNamesAreValid}
                  />
                </ButtonWrapper>
              </>
            )}
          </ButtonArea>
        </TitleBar>
        <Description>{vt.description}</Description>
        {!labelsCanBeDeleted ? (
          <MessageAreaWrapper>
            <MessageArea messages={message} />
          </MessageAreaWrapper>
        ) : (
          undefined
        )}
        <ErrorArea>
          {showErrorString ? (
            <FormHelperText error={true}>{vt.messages.error}</FormHelperText>
          ) : (
            undefined
          )}
        </ErrorArea>
        <MuiPaper bgColor={colorDef.paperLighter} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                {mode === 'edit' ? <TableCellDelete /> : undefined}
                <TableCell>{vt.header.labelName}</TableCell>
                <TableCell align='center'>{vt.header.color}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labelsWithHandler.map(([label, onInput, deleteOnClick], idx) => (
                <MuiTableRow key={label.id}>
                  {mode === 'edit' ? (
                    <TableCellDelete align='center'>
                      <IconButton
                        disabled={!label.canBeDeleted}
                        onClick={deleteOnClick}
                      >
                        <RemoveCircleIcon disabled={!label.canBeDeleted} />
                      </IconButton>
                    </TableCellDelete>
                  ) : (
                    undefined
                  )}
                  <TableCell>
                    {mode === 'view' ? (
                      label.name
                    ) : (
                      <>
                        <MuiInput
                          label={vt.inputLabel}
                          value={label.name}
                          onInput={onInput}
                          minLength={1}
                          maxLength={maxStringLength.labelName}
                          autoFocus={idx === labelsWithHandler.size - 1}
                        />
                        <FormHelperText>
                          {isInRange(
                            1,
                            maxStringLength.labelName
                          )(label.name.length)
                            ? ''
                            : viewTexts.form.stringLengthCheckMessage(
                                vt.inputLabel,
                                1,
                                maxStringLength.labelName
                              )}
                        </FormHelperText>
                      </>
                    )}
                  </TableCell>
                  <TableCell align='center'>
                    <LabelColorCell
                      mode={mode}
                      id={label.id}
                      hue={label.hue}
                      changeLabelColor={changeLabelColor}
                    />
                  </TableCell>
                </MuiTableRow>
              ))}
            </TableBody>
          </Table>
        </MuiPaper>
        {mode === 'edit' ? (
          <ButtonArea>
            <ButtonWrapper>
              <MuiButton
                label={vt.buttons.add}
                color='primary'
                onClick={addLabelOnClick}
              />
            </ButtonWrapper>
          </ButtonArea>
        ) : (
          undefined
        )}
      </Root>
    );
  }
);

LabelsView.displayName = 'LabelsView';
